import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Checkbox,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip
} from '@material-ui/core'
import { Close, CreateNewFolder, Delete, Save } from '@material-ui/icons'
import TreeView from './TreeView.js'
import { getItemIcon } from '../icons/index.js'
import redux from '../redux/index.js'
import {
  cleanFileName,
  canOpenDocument,
  openDocument,
  constructDocumentParams,
  formatResourceString,
  getFileExtension,
  PDF_CONVERTIBLE_FILES,
  isSelected,
  sortFileItems
} from '../util.js'
import { FOOTER_BACKGROUND } from '../styles.js'
import {
  isOutlook,
  isOutlookComposeMode,
  isOutlookReadMode,
  emailFileNameOutlookReadMode,
  readFileAttachments
} from '../lib/office.js'

const DROP_DEBUG = process.env.NODE_ENV === 'development'

const FileView = ({
  strings,
  selected,
  selectedFileNode,
  selectedFileNodes,
  expand,
  items,
  page,
  onSetSelectedFolder,
  onGetRootFolder,
  onGetSubFolders,
  onSetSelectedFile,
  onSetSelectedFiles,
  searchValue,
  searchResults,
  isSearching,
  userData,
  showSearchResults,
  onSearchValueChange,
  onStartSearch,
  onResetSearch,
  onShowRenameAttachmentsDialog,
  onAttachFile,
  onAttachFiles,
  onAttachFileAsPdf,
  onAttachFileAsPdfAndSaveCopy,
  onSaveFileAsPdfCopy,
  onCreateFolder,
  onRenameItem,
  onDeleteItem,
  onDeleteItemPermanently,
  onDropSaveCurrentEmail,
  onDropSaveFiles
}) => {
  const mount = useRef(false)
  const autoSearchSelectionRef = useRef('')
  const fileSelectionAnchorRef = useRef(null)
  const [attachments, setAttachments] = useState([])
  const [contextMenu, setContextMenu] = useState(null)
  const [createFolderDialog, setCreateFolderDialog] = useState({
    open: false,
    item: null,
    value: '',
    hadIllegalInput: false
  })
  const [renameDialog, setRenameDialog] = useState({
    open: false,
    item: null,
    value: '',
    extension: '',
    isExtensionEditing: false,
    hadIllegalInput: false
  })
  const renameInputRef = useRef(null)
  const renameExtensionInputRef = useRef(null)
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    item: null,
    confirmed: false,
    permanent: false
  })
  const [overwriteConfirmDialog, setOverwriteConfirmDialog] = useState({
    open: false,
    fileName: ''
  })
  const overwriteConfirmResolverRef = useRef(null)
  const selectedFileIdSet = new Set((selectedFileNodes || []).map((file) => file?.id).filter(Boolean))
  const isSingleSelected = isSelected(selectedFileNode)

  const isFileTreeItemSelected = (item) => {
    if (isOutlook() && item?.type === 'file' && selectedFileIdSet.size > 0) {
      return selectedFileIdSet.has(item.id)
    }
    return isSingleSelected(item)
  }

  const canCreateFolderOnItem = (item) => {
    return page !== 'templates' && (item?.type === 'folder' || item?.type === 'rootfolder')
  }

  const canRenameItem = (item) => {
    return item?.type === 'folder' || item?.type === 'file'
  }

  const canDeleteItem = (item) => {
    return item?.type === 'file' || item?.type === 'folder'
  }

  const canAttachToEmail = (item) => {
    return !!item && item.type === 'file' && isOutlookComposeMode()
  }

  const canSaveAsPdfCopy = (item) => {
    if (!item || item.type !== 'file') {
      return false
    }
    const ext = getFileExtension(String(item.name || ''))
    return PDF_CONVERTIBLE_FILES.includes(ext)
  }

  const canAttachAsPdf = (item) => {
    return canAttachToEmail(item) && canSaveAsPdfCopy(item)
  }

  useEffect(() => {
    if (!isOutlook() || (!isOutlookReadMode() && !isOutlookComposeMode())) {
      setAttachments([])
      return
    }

    const refreshAttachments = () => {
      readFileAttachments((err, result) => {
        if (err) {
          return setAttachments([])
        }
        setAttachments(Array.isArray(result) ? result : [])
      })
    }

    refreshAttachments()

    const onItemChanged = () => {
      refreshAttachments()
    }

    if (Office?.context?.mailbox?.addHandlerAsync && Office?.EventType?.ItemChanged) {
      Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, onItemChanged)
    }

    return () => {
      if (Office?.context?.mailbox?.removeHandlerAsync && Office?.EventType?.ItemChanged) {
        Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, { handler: onItemChanged })
      }
    }
  }, [selected.current?.id])

  useEffect(() => {
    if (isOutlook()) {
      if (!mount.current || mount.current !== selected.current?.id) {
        const subFolderFound = items[0]?.children?.find(({ path }) => path === 'files/E-post' || path === 'files/E-mail' || path === 'files/e-post' || path === 'files/email')
        if (subFolderFound) {
          onSetSelectedFolder(subFolderFound)
          mount.current = selected.current.id
          if (!expand[subFolderFound.id]) {
            onGetSubFolders(selected.current, subFolderFound)
          }
        }
      }
    }
  }, [onSetSelectedFolder, onGetSubFolders, selected.current, items])

  useEffect(() => {
    const current = selected.current
    const selectionKey = current ? `${current.type}:${current.id}` : ''

    if (!selectionKey) {
      autoSearchSelectionRef.current = ''
      return
    }

    if (autoSearchSelectionRef.current === selectionKey) {
      return
    }
    autoSearchSelectionRef.current = selectionKey

    const activeQuery = String(searchValue || '').trim()
    if (activeQuery.length >= 2) {
      onStartSearch(current, activeQuery)
    }
  }, [selected.current, searchValue, onStartSearch])

  useEffect(() => {
    if (!renameDialog.open) {
      return
    }

    const id = window.requestAnimationFrame(() => {
      const input = renameDialog.isExtensionEditing
        ? renameExtensionInputRef.current
        : renameInputRef.current
      if (!input || typeof input.focus !== 'function') {
        return
      }

      input.focus()
      const valueLength = String(renameDialog.value || '').length
      if (typeof input.setSelectionRange === 'function') {
        input.setSelectionRange(valueLength, valueLength)
      }
    })

    return () => {
      window.cancelAnimationFrame(id)
    }
  }, [renameDialog.open, renameDialog.isExtensionEditing])

  useEffect(() => {
    return () => {
      if (typeof overwriteConfirmResolverRef.current === 'function') {
        overwriteConfirmResolverRef.current(false)
        overwriteConfirmResolverRef.current = null
      }
    }
  }, [])

  const requestOverwriteConfirmation = (fileName) => {
    return new Promise((resolve) => {
      overwriteConfirmResolverRef.current = resolve
      setOverwriteConfirmDialog({
        open: true,
        fileName: String(fileName || '')
      })
    })
  }

  const resolveOverwriteConfirmation = (confirmed) => {
    if (typeof overwriteConfirmResolverRef.current === 'function') {
      overwriteConfirmResolverRef.current(!!confirmed)
      overwriteConfirmResolverRef.current = null
    }
    setOverwriteConfirmDialog({
      open: false,
      fileName: ''
    })
  }

  const onSetSelectedItem = (e, fileItem) => {
    const isModifierSelection =
      isOutlook() &&
      fileItem?.type === 'file' &&
      e?.type === 'click' &&
      (e?.button === 0 || typeof e?.button === 'undefined') &&
      (e?.ctrlKey || e?.metaKey || e?.shiftKey)

    if (isModifierSelection) {
      const fileItems = flattenVisibleFileItems(items, expand)
      const current = Array.isArray(selectedFileNodes) ? selectedFileNodes : []
      const currentMap = new Map(current.map((file) => [file.id, file]))

      let next = []
      const anchorId = fileSelectionAnchorRef.current || selectedFileNode?.id || fileItem.id
      if (e.shiftKey) {
        const range = getFileSelectionRange(fileItems, anchorId, fileItem.id)
        if (e.ctrlKey || e.metaKey) {
          range.forEach((file) => currentMap.set(file.id, file))
          next = Array.from(currentMap.values())
        } else {
          next = range
        }
      } else {
        if (currentMap.has(fileItem.id)) {
          currentMap.delete(fileItem.id)
        } else {
          currentMap.set(fileItem.id, fileItem)
        }
        next = Array.from(currentMap.values())
      }

      onSetSelectedFiles(next)
      onSetSelectedFile(fileItem)
      if (e.shiftKey) {
        if (!fileSelectionAnchorRef.current) {
          fileSelectionAnchorRef.current = anchorId
        }
      } else {
        fileSelectionAnchorRef.current = fileItem.id
      }
      return
    }

    const { current, previous } = selected
    if (fileItem.type === 'rootfolder') {
      fileSelectionAnchorRef.current = null
      onSetSelectedFiles([])
      onSetSelectedFolder(fileItem)
      onGetRootFolder(current)
    } else if (fileItem.type === 'folder') {
      fileSelectionAnchorRef.current = null
      onSetSelectedFiles([])
      onSetSelectedFolder(fileItem)
      if (!expand[fileItem.id]) {
        onGetSubFolders(current, fileItem)
      }
    } else if (fileItem.type === 'file') {
      fileSelectionAnchorRef.current = fileItem.id
      onSetSelectedFiles([fileItem])
      onSetSelectedFile(fileItem)
      if ((e === null || e?.detail === 2) && canOpenDocument(fileItem.uri)) {
        const context = current && current.type !== 'templates' ? current : previous
        const params = constructDocumentParams(userData, context)
        openDocument(fileItem.uri, params)
      }
    }
  }

  const canDropOnItem = (item) => {
    return (item.type === 'folder' || item.type === 'rootfolder') && isOutlook()
  }

  const onDropOnItem = async (event, folderItem) => {
    logDrop('attachment-drop: onDropOnItem fired', {
      folderId: folderItem?.id,
      folderName: folderItem?.name,
      selectedContext: selected.current?.type
    })

    if (!selected.current) return

    const droppedNames = getDroppedAttachmentNames(event)
    const droppedFiles = getDroppedFiles(event)
    const hasFilePayload = droppedFiles.length > 0
    logDrop('attachment-drop: parsed dropped names', droppedNames)
    if (droppedNames.length === 0 && !hasFilePayload) {
      logDrop('attachment-drop: no dropped attachment names detected, treating drop as email item')
      onSetSelectedFolder(folderItem, { toggleExpand: false })
      await onDropSaveCurrentEmail(selected.current, folderItem, requestOverwriteConfirmation)
      return
    }

    if (attachments.length === 0) {
      logDrop('attachment-drop: no current mail attachments loaded, opening save dialog for dropped email file')
      onSetSelectedFolder(folderItem, { toggleExpand: false })
      await onDropSaveFiles(selected.current, folderItem, droppedFiles, requestOverwriteConfirmation)
      return
    }

    const byName = attachments.reduce((acc, attachment) => {
      const key = normalizeAttachmentName(attachment.fileName)
      if (!key) return acc
      if (!Array.isArray(acc[key])) acc[key] = []
      acc[key].push(attachment)
      return acc
    }, {})

    const matched = []
    droppedNames.forEach((name) => {
      const key = normalizeAttachmentName(name)
      const bucket = byName[key]
      if (Array.isArray(bucket) && bucket.length > 0) {
        matched.push(bucket.shift())
      }
    })

    logDrop('attachment-drop: matched attachments', matched.map(a => a.fileName))

    const dropInfo = {
      requestedCount: droppedNames.length,
      matchedCount: matched.length
    }

    if (matched.length > 0) {
      onShowRenameAttachmentsDialog(selected.current, folderItem, matched, dropInfo)
    } else {
      if (hasFilePayload) {
        logDrop('attachment-drop: dropped file payload without attachment name matches, opening save dialog for dropped email file')
        onSetSelectedFolder(folderItem, { toggleExpand: false })
        await onDropSaveFiles(selected.current, folderItem, droppedFiles, requestOverwriteConfirmation)
      } else {
        logDrop('attachment-drop: no name matches found, opening full dialog')
        onShowRenameAttachmentsDialog(selected.current, folderItem)
      }
    }
  }

  const onItemContextMenu = (event, item) => {
    event.preventDefault()
    const canShowAnyAction =
      canAttachToEmail(item) ||
      canSaveAsPdfCopy(item) ||
      canCreateFolderOnItem(item) ||
      canRenameItem(item) ||
      canDeleteItem(item)
    if (!canShowAnyAction) {
      setContextMenu(null)
      return
    }
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      item
    })
  }

  const onCloseContextMenu = () => {
    setContextMenu(null)
  }

  const onCreateFolderFromContextMenu = () => {
    const item = contextMenu?.item
    if (!item || !selected.current || !canCreateFolderOnItem(item)) {
      onCloseContextMenu()
      return
    }

    setCreateFolderDialog({
      open: true,
      item,
      value: '',
      hadIllegalInput: false
    })
    onCloseContextMenu()
  }

  const onAttachToEmailFromContextMenu = () => {
    const item = contextMenu?.item
    if (!item || !canAttachToEmail(item)) {
      onCloseContextMenu()
      return
    }

    const selectedFiles = (selectedFileNodes || []).filter((file) => file?.type === 'file')
    const attachItems = selectedFiles.length > 1 && selectedFiles.some((file) => file.id === item.id)
      ? selectedFiles
      : [item]

    if (attachItems.length > 1) {
      onAttachFiles(attachItems)
    } else {
      onAttachFile(item)
    }
    onCloseContextMenu()
  }

  const onAttachAsPdfFromContextMenu = () => {
    const item = contextMenu?.item
    if (!item || !canAttachAsPdf(item)) {
      onCloseContextMenu()
      return
    }

    onAttachFileAsPdf(item)
    onCloseContextMenu()
  }

  const onAttachAsPdfAndSaveCopyFromContextMenu = () => {
    const item = contextMenu?.item
    if (!item || !canAttachAsPdf(item)) {
      onCloseContextMenu()
      return
    }

    onAttachFileAsPdfAndSaveCopy(item)
    onCloseContextMenu()
  }

  const onSaveAsPdfCopyFromContextMenu = () => {
    const item = contextMenu?.item
    if (!item || !canSaveAsPdfCopy(item)) {
      onCloseContextMenu()
      return
    }

    onSaveFileAsPdfCopy(item)
    onCloseContextMenu()
  }

  const onCloseCreateFolderDialog = () => {
    setCreateFolderDialog({ open: false, item: null, value: '', hadIllegalInput: false })
  }

  const onConfirmCreateFolderDialog = () => {
    const item = createFolderDialog.item
    if (!item || !selected.current) {
      onCloseCreateFolderDialog()
      return
    }

    const rawValue = String(createFolderDialog.value || '')
    const hasIllegalInput = hasIllegalSharePointNameChars(rawValue)
    const trimmed = rawValue.trim()
    if (!trimmed || hasIllegalInput) {
      return
    }

    onCreateFolder(selected.current, item, trimmed)
    onCloseCreateFolderDialog()
  }

  const onRenameFromContextMenu = () => {
    const item = contextMenu?.item
    if (!item || !selected.current || !canRenameItem(item)) {
      onCloseContextMenu()
      return
    }
    const cleanedName = cleanFileName(String(item.name || ''))
    const extension = getLockedFileExtension(cleanedName, item.type)
    setRenameDialog({
      open: true,
      item,
      value: getRenameEditableName(cleanedName, item.type),
      extension,
      isExtensionEditing: false,
      hadIllegalInput: false
    })
    onCloseContextMenu()
  }

  const onDeleteFromContextMenu = () => {
    const item = contextMenu?.item
    if (!item || !selected.current || !canDeleteItem(item)) {
      onCloseContextMenu()
      return
    }

    onDeleteItem(selected.current, item)
    onCloseContextMenu()
  }

  // const onDeletePermanentlyFromContextMenu = () => {
  //   const item = contextMenu?.item
  //   if (!item || !selected.current || !canDeleteItem(item)) {
  //     onCloseContextMenu()
  //     return
  //   }

  //   setDeleteDialog({ open: true, item, confirmed: false, permanent: true })
  //   onCloseContextMenu()
  // }

  const onCloseRenameDialog = () => {
    setRenameDialog({ open: false, item: null, value: '', extension: '', isExtensionEditing: false, hadIllegalInput: false })
  }

  const onConfirmRenameDialog = () => {
    const item = renameDialog.item
    if (!item || !selected.current) {
      onCloseRenameDialog()
      return
    }

    const oldName = String(item.name || '').trim()
    const extension = normalizeEditableExtension(renameDialog.extension)
    const rawEditableName = String(renameDialog.value || '')
    const hasIllegalInput = hasIllegalSharePointNameChars(rawEditableName)
    const trimmedEditableName = rawEditableName.trim()
    const nextName = composeRenameFileName(trimmedEditableName, extension)
    const hasKnownCollision = hasNameCollisionInKnownFolder(item, nextName, items)

    if (!trimmedEditableName || hasIllegalInput) {
      return
    }

    if (nextName === oldName) {
      onCloseRenameDialog()
      return
    }

    if (hasKnownCollision) {
      return
    }

    onRenameItem(selected.current, item, nextName)
    onCloseRenameDialog()
  }

  const onCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, item: null, confirmed: false, permanent: false })
  }

  const onConfirmDeleteDialog = () => {
    const item = deleteDialog.item
    if (item && selected.current) {
      onDeleteItemPermanently(selected.current, item)
    }
    onCloseDeleteDialog()
  }

  const renderContextMenu = () => {
    const item = contextMenu?.item || null
    const showComposeAttach = canAttachToEmail(item)
    const showComposeAttachAsPdf = canAttachAsPdf(item)
    const showSavePdfCopy = canSaveAsPdfCopy(item)
    const showCreateFolder = canCreateFolderOnItem(item)
    const showRename = canRenameItem(item)
    const showDelete = canDeleteItem(item)
    const showReadSection = showCreateFolder || showRename || showDelete
    const showActionSection = showSavePdfCopy || showComposeAttach || showComposeAttachAsPdf

    return (
      <Menu
        keepMounted
        open={Boolean(contextMenu)}
        onClose={onCloseContextMenu}
        PaperProps={{ style: contextMenuPaperStyle }}
        MenuListProps={{ style: contextMenuListStyle }}
        anchorReference='anchorPosition'
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {showCreateFolder ? <MenuItem onClick={onCreateFolderFromContextMenu} style={contextMenuItemStyle}>{strings.create_folder}</MenuItem> : null}
        {showRename ? <MenuItem onClick={onRenameFromContextMenu} style={contextMenuItemStyle}>{strings.rename}</MenuItem> : null}
        {showDelete ? <MenuItem onClick={onDeleteFromContextMenu} style={contextMenuItemStyle}>{strings.move_to_trash || 'Move to trash'}</MenuItem> : null}
        {/* {showDelete ? <MenuItem onClick={onDeletePermanentlyFromContextMenu} style={contextMenuItemStyle}>{strings.remove_permanently || 'Remove permanently'}</MenuItem> : null} */}
        {showReadSection && showActionSection ? <Divider /> : null}
        {showSavePdfCopy ? <MenuItem onClick={onSaveAsPdfCopyFromContextMenu} style={contextMenuItemStyle}>{strings.save_pdf_copy || 'Save a copy as PDF'}</MenuItem> : null}
        {showComposeAttach ? <MenuItem onClick={onAttachToEmailFromContextMenu} style={contextMenuItemStyle}>{strings.add_to_email || 'Add to email'}</MenuItem> : null}
        {showComposeAttachAsPdf ? <MenuItem onClick={onAttachAsPdfFromContextMenu} style={contextMenuItemStyle}>{strings.attach_as_pdf || 'Attach as PDF'}</MenuItem> : null}
        {showComposeAttachAsPdf ? <MenuItem onClick={onAttachAsPdfAndSaveCopyFromContextMenu} style={contextMenuItemStyle}>{strings.attach_as_pdf_save_copy || 'Attach as PDF and save copy'}</MenuItem> : null}
      </Menu>
    )
  }

  const renderRenameDialog = () => {
    const item = renameDialog.item
    const itemType = item?.type
    const hasExtensionControl = renameDialog.item?.type === 'file'
    const extension = normalizeEditableExtension(renameDialog.extension)
    const hasLockedExtension = hasExtensionControl && extension.length > 0
    const oldName = String(item?.name || '')
    const currentRelativePath = getDocumentLibraryRelativePath(item) || oldName
    const trimmedEditableName = String(renameDialog.value || '').trim()
    const nextName = composeRenameFileName(trimmedEditableName, extension)
    const hasIllegalInput = renameDialog.hadIllegalInput
    const hasKnownCollision = hasNameCollisionInKnownFolder(item, nextName, items)
    const isRenameDisabled = trimmedEditableName.length === 0 || nextName === oldName || hasKnownCollision || hasIllegalInput
    const currentLeafName = hasExtensionControl
      ? composeRenameFileName(getRenameEditableName(oldName, itemType), extension)
      : oldName
    const nextLeafName = composeRenameFileName(trimmedEditableName || getRenameEditableName(oldName, itemType), extension)
    const renamePreviewPath = formatInfoBoxRelativePath(
      replaceLastRelativePathElement(currentRelativePath, nextLeafName || currentLeafName),
      { trailingSlash: false }
    )
    const helperMessage = hasKnownCollision
      ? strings.rename_name_exists
      : (hasIllegalInput ? (strings.rename_invalid_chars || strings.create_folder_invalid_chars || strings.rename_invalid_chars_removed) : '')

    return (
      <Dialog
        open={renameDialog.open}
        onClose={onCloseRenameDialog}
        maxWidth='xs'
        fullWidth
        PaperProps={{ onClick: stopEventPropagation }}
      >
        <DialogTitle style={fileDialogTitleStyle}>{strings.rename}</DialogTitle>
        <DialogContent style={dialogInputContentStyle}>
          <div style={deleteDetailsCardStyle}>
            <div style={deleteDetailsRowStyle}>
              <span style={deletePathValueStyle}>{renamePreviewPath}</span>
            </div>
          </div>
          <div style={renameInputRowStyle}>
            <input
              ref={renameInputRef}
              autoFocus
              type='text'
              value={renameDialog.value}
              onChange={(e) => {
                const rawValue = String(e.target.value || '')
                setRenameDialog((prev) => ({
                  ...prev,
                  value: rawValue,
                  hadIllegalInput: hasIllegalSharePointNameChars(rawValue)
                }))
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isRenameDisabled) {
                  e.preventDefault()
                  onConfirmRenameDialog()
                }
              }}
              style={renameInputStyle(hasLockedExtension)}
            />
            {hasExtensionControl
              ? (
                  renameDialog.isExtensionEditing
                    ? (
                      <input
                        ref={renameExtensionInputRef}
                        type='text'
                        value={extension}
                        onChange={(e) => {
                          const rawValue = String(e.target.value || '')
                          setRenameDialog((prev) => ({
                            ...prev,
                            extension: normalizeEditableExtension(rawValue)
                          }))
                        }}
                        onBlur={() => {
                          setRenameDialog((prev) => ({
                            ...prev,
                            isExtensionEditing: false
                          }))
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !isRenameDisabled) {
                            e.preventDefault()
                            onConfirmRenameDialog()
                            return
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault()
                            setRenameDialog((prev) => ({
                              ...prev,
                              isExtensionEditing: false
                            }))
                          }
                        }}
                        style={renameExtensionInputStyle(extension)}
                      />
                      )
                    : (
                      <button
                        type='button'
                        onClick={() => {
                          setRenameDialog((prev) => ({
                            ...prev,
                            isExtensionEditing: true
                          }))
                        }}
                        style={renameExtensionStyle}
                      >
                        {extension || '.'}
                      </button>
                      )
                )
              : null}
          </div>
          {(hasExtensionControl && renameDialog.isExtensionEditing)
            ? <div style={renameExtensionHintStyle}>{strings.rename_extension_warning || 'Changing file extension can affect how the file opens.'}</div>
            : null}
          {helperMessage ? <div style={renameHelperStyle(hasKnownCollision || hasIllegalInput)}>{helperMessage}</div> : null}
        </DialogContent>
        <DialogActions style={deleteDialogActionsStyle}>
          <Button onClick={onCloseRenameDialog} style={deleteActionButtonStyle(false)}>
            <Close style={dialogActionIconStyle} />
            {strings.cancel}
          </Button>
          <Button
            onClick={onConfirmRenameDialog}
            disabled={isRenameDisabled}
            style={renameActionButtonStyle(isRenameDisabled)}
          >
            <Save style={dialogActionIconStyle} />
            {strings.save}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const renderCreateFolderDialog = () => {
    const trimmed = String(createFolderDialog.value || '').trim()
    const hasIllegalInput = createFolderDialog.hadIllegalInput
    const disableCreate = trimmed.length === 0 || hasIllegalInput
    const helperMessage = hasIllegalInput ? (strings.create_folder_invalid_chars || strings.rename_invalid_chars_removed) : ''
    const parentRelativePath = getDocumentLibraryRelativePath(createFolderDialog.item)
    const pendingFolderName = String(createFolderDialog.value || '').trim()
    const createFolderPreviewPath = formatInfoBoxRelativePath(
      joinRelativePathElements(parentRelativePath, pendingFolderName),
      { trailingSlash: pendingFolderName.length === 0 }
    )

    return (
      <Dialog
        open={createFolderDialog.open}
        onClose={onCloseCreateFolderDialog}
        maxWidth='xs'
        fullWidth
        PaperProps={{ onClick: stopEventPropagation }}
      >
        <DialogTitle style={fileDialogTitleStyle}>{strings.create_folder}</DialogTitle>
        <DialogContent style={dialogInputContentStyle}>
          <div style={deleteDetailsCardStyle}>
            <div style={deleteDetailsRowStyle}>
              <span style={deletePathValueStyle}>{createFolderPreviewPath}</span>
            </div>
          </div>
          <input
            autoFocus
            type='text'
            placeholder={strings.foldername}
            value={createFolderDialog.value}
            onChange={(e) => {
              const rawValue = String(e.target.value || '')
              setCreateFolderDialog((prev) => ({
                ...prev,
                value: rawValue,
                hadIllegalInput: hasIllegalSharePointNameChars(rawValue)
              }))
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !disableCreate) {
                e.preventDefault()
                onConfirmCreateFolderDialog()
              }
            }}
            style={renameInputStyle(false)}
          />
          {helperMessage ? <div style={renameHelperStyle(true)}>{helperMessage}</div> : null}
        </DialogContent>
        <DialogActions style={deleteDialogActionsStyle}>
          <Button onClick={onCloseCreateFolderDialog} style={deleteActionButtonStyle(false)}>
            <Close style={dialogActionIconStyle} />
            {strings.cancel}
          </Button>
          <Button
            onClick={onConfirmCreateFolderDialog}
            disabled={disableCreate}
            style={createActionButtonStyle(disableCreate)}
          >
            <CreateNewFolder style={dialogActionIconStyle} />
            {strings.create_folder}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const renderDeleteDialog = () => {
    const item = deleteDialog.item
    const itemName = String(item?.name || '')
    const relativePath = formatInfoBoxRelativePath(getDocumentLibraryRelativePath(item) || itemName)
    const itemTypeLabel = item?.type === 'folder' || item?.type === 'rootfolder'
      ? (strings.delete_item_type_folder || 'Folder')
      : (strings.delete_item_type_file || 'File')
    const deleteDialogTitle = `${strings.remove_permanently || 'Remove permanently'} ${itemTypeLabel}`
    const folderContentCount = getFolderContentCount(item)
    const hasFolderContent = folderContentCount > 0
    const folderWarning = hasFolderContent
      ? (strings.delete_folder_has_content_with_count || 'This folder contains %1 item(s). Deleting it will remove all included files and folders.')
          .replace('%1', String(folderContentCount))
      : ''
    const deleteDisabled = !deleteDialog.confirmed

    return (
      <Dialog
        open={deleteDialog.open}
        onClose={onCloseDeleteDialog}
        maxWidth='xs'
        fullWidth
        PaperProps={{ onClick: stopEventPropagation }}
      >
        <DialogTitle style={fileDialogTitleStyle}>{deleteDialogTitle}</DialogTitle>
        <DialogContent style={dialogInputContentStyle}>
          <div style={deleteDetailsCardStyle}>
            <div style={deleteDetailsRowStyle}>
              <span style={deletePathValueStyle}>{relativePath}</span>
            </div>
          </div>
          {hasFolderContent
            ? <div style={deleteWarningStyle}>{folderWarning}</div>
            : null}
          <FormControlLabel
            control={
              <Checkbox
                checked={!!deleteDialog.confirmed}
                onChange={(event) => {
                  const checked = !!event.target.checked
                  setDeleteDialog((prev) => ({ ...prev, confirmed: checked }))
                }}
                color='secondary'
              />
            }
            label={<span style={deleteConfirmLabelStyle}>{strings.delete_confirm_checkbox}</span>}
            style={deleteConfirmStyle}
          />
        </DialogContent>
        <DialogActions style={deleteDialogActionsStyle}>
          <Button onClick={onCloseDeleteDialog} style={deleteActionButtonStyle(false)}>
            <Close style={dialogActionIconStyle} />
            {strings.cancel}
          </Button>
          <Button
            onClick={onConfirmDeleteDialog}
            disabled={deleteDisabled}
            style={deleteActionButtonStyle(true, deleteDisabled)}
          >
            <Delete style={dialogActionIconStyle} />
            {strings.remove_permanently || 'Remove permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const renderOverwriteConfirmDialog = () => {
    const fileName = String(overwriteConfirmDialog.fileName || '').trim()
    const message = formatResourceString(
      strings.overwrite_file_confirm || 'The file "%1" already exists. Click Overwrite to replace it or Abort to cancel.',
      fileName
    )

    return (
      <Dialog
        open={overwriteConfirmDialog.open}
        onClose={() => resolveOverwriteConfirmation(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{ onClick: stopEventPropagation }}
      >
        <DialogTitle style={fileDialogTitleStyle}>{strings.overwrite_dialog_title || 'Overwrite file?'}</DialogTitle>
        <DialogContent style={dialogInputContentStyle}>
          <div style={deleteWarningStyle}>{message}</div>
        </DialogContent>
        <DialogActions style={deleteDialogActionsStyle}>
          <Button onClick={() => resolveOverwriteConfirmation(false)} style={deleteActionButtonStyle(false)}>
            <Close style={dialogActionIconStyle} />
            {strings.abort_action || strings.cancel || 'Abort'}
          </Button>
          <Button onClick={() => resolveOverwriteConfirmation(true)} style={createActionButtonStyle(false)}>
            <Save style={dialogActionIconStyle} />
            {strings.overwrite_action || 'Overwrite'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (showSearchResults) {
    return (
      <>
        <ListView
          items={searchResults}
          isSearching={isSearching}
          strings={strings}
          sortItems={sortFileItems}
          isSelected={isFileTreeItemSelected}
          onItemSelected={onSetSelectedItem}
          onItemContextMenu={onItemContextMenu}
          onClose={onResetSearch}
        />
        {renderContextMenu()}
        {renderCreateFolderDialog()}
        {renderRenameDialog()}
        {renderDeleteDialog()}
        {renderOverwriteConfirmDialog()}
      </>
    )
  } else {
    return (
      <>
        <TreeView
          items={items}
          expand={expand}
          scrollSelectedIntoView={isOutlook()}
          scrollExpandedFolderIntoView
          canDropOnItem={canDropOnItem}
          onDropOnItem={onDropOnItem}
          sortItems={sortFileItems}
          isSelected={isFileTreeItemSelected}
          onItemSelected={onSetSelectedItem}
          onItemContextMenu={onItemContextMenu}
        />
        {renderContextMenu()}
        {renderCreateFolderDialog()}
        {renderRenameDialog()}
        {renderDeleteDialog()}
        {renderOverwriteConfirmDialog()}
      </>
    )
  }
}

function hasNameCollisionInKnownFolder (item, candidateName, treeItems) {
  if (!item) return false

  const siblings = getKnownSiblingItems(item, treeItems || [])
  if (!Array.isArray(siblings) || siblings.length === 0) {
    return false
  }

  const candidate = String(candidateName || '').trim().toLowerCase()
  if (candidate.length === 0) {
    return false
  }

  return siblings.some((sibling) => {
    if (!sibling || sibling.id === item.id || sibling.type !== item.type) {
      return false
    }
    return String(sibling.name || '').trim().toLowerCase() === candidate
  })
}

function getKnownSiblingItems (item, treeItems) {
  if (!Array.isArray(treeItems) || treeItems.length === 0 || !item) {
    return []
  }

  const stack = [...treeItems]
  while (stack.length > 0) {
    const current = stack.shift()
    const children = Array.isArray(current?.children) ? current.children : []
    const found = children.find((child) => child?.id === item.id)
    if (found) {
      return children
    }
    stack.push(...children)
  }

  return []
}

const ListView = ({
  items,
  strings,
  isSearching,
  onItemSelected,
  onItemContextMenu,
  isSelected,
  sortItems,
  onClose
}) => {
  const renderItem = (item) => {
    const ItemIcon = getItemIcon(item)
    const primary = (<span style={listItemTextStyle}>{item.name}</span>)
    const tooltipTitle = getDocumentLibraryRelativePath(item) || item.name

    return (
      <div key={item.id}>
        <Tooltip
          title={<span style={searchResultTooltipTextStyle}>{tooltipTitle}</span>}
          enterDelay={700}
          enterNextDelay={700}
          arrow
          placement='top-start'
          PopperProps={{
            disablePortal: true,
            modifiers: {
              offset: {
                enabled: true,
                offset: '0, -22'
              }
            }
          }}
        >
          <ListItem
            onClick={(e) => onItemSelected(e, item)}
            onContextMenu={(e) => onItemContextMenu(e, item)}
            button
            selected={isSelected(item)}
            style={listItemStyle}
          >
            <ListItemIcon>
              <ItemIcon />
            </ListItemIcon>
            <ListItemText
              primary={primary}
            />
          </ListItem>
        </Tooltip>
      </div>
    )
  }

  const renderItems = () => {
    if (!items.length && !isSearching) {
      return (
        <ListItem style={listItemStyle}>
          <ListItemText primary={strings.file_search_no_results} />
        </ListItem>
      )
    } else {
      return sortItems(items).map(renderItem)
    }
  }

  return (
    <List style={{ padding: 0 }}>
      {renderItems()}
    </List>
  )
}

const listItemStyle = {
  paddingLeft: 10,
  paddingRight: 0,
  paddingTop: 1,
  paddingBottom: 1
}

const listItemTextStyle = {
  marginLeft: -34,
  fontSize: '0.68rem',
  lineHeight: 1.1
}

const renameInputRowStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: 6
}

const renameInputStyle = (hasLockedExtension) => ({
  width: hasLockedExtension ? 'auto' : '100%',
  flex: 1,
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid #c7cdd1',
  fontSize: '0.9rem',
  boxSizing: 'border-box'
})

const renameExtensionStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 34,
  padding: '0 8px',
  border: '1px solid #c7cdd1',
  borderRadius: 4,
  background: '#f5f7f8',
  color: '#44515a',
  fontSize: '0.9rem',
  lineHeight: 1,
  cursor: 'text'
}

const renameExtensionInputStyle = (extension) => ({
  width: `${Math.min(120, Math.max(38, (String(extension || '.').length * 8) + 16))}px`,
  minHeight: 34,
  padding: '0 8px',
  borderRadius: 4,
  border: '1px solid #c7cdd1',
  lineHeight: 1,
  fontSize: '0.9rem',
  boxSizing: 'border-box'
})

const renameExtensionHintStyle = {
  marginTop: 6,
  fontSize: '0.76rem',
  color: '#5c6972',
  lineHeight: 1.25
}

const fileDialogTitleStyle = {
  background: FOOTER_BACKGROUND,
  color: 'white',
  fontSize: '0.82rem',
  lineHeight: 1.1,
  padding: '7px 14px',
  minHeight: 28
}

const dialogInputContentStyle = {
  paddingTop: 12
}

const renameHelperStyle = (isError) => {
  return {
    marginTop: 6,
    fontSize: '0.8rem',
    color: isError ? '#b71c1c' : '#555'
  }
}

const searchResultTooltipTextStyle = {
  fontSize: 'inherit',
  lineHeight: 'inherit',
  padding: 0,
  display: 'inline'
}

const contextMenuPaperStyle = {
  minWidth: 140
}

const contextMenuListStyle = {
  paddingTop: 2,
  paddingBottom: 2
}

const contextMenuItemStyle = {
  minHeight: 28,
  fontSize: '0.8rem',
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: 10,
  paddingRight: 10
}

const deleteConfirmStyle = {
  marginTop: 8,
  marginLeft: 0
}

const deleteConfirmLabelStyle = {
  fontSize: '0.76rem',
  lineHeight: 1.2
}

const deleteDetailsCardStyle = {
  background: '#f4f7f9',
  border: '1px solid #d4dde3',
  borderRadius: 4,
  padding: '8px 10px',
  marginBottom: 8
}

const deleteDetailsRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  fontSize: '0.8rem',
  color: '#2b3f4c',
  lineHeight: 1.3
}

const deletePathValueStyle = {
  wordBreak: 'break-word'
}

const deleteWarningStyle = {
  color: '#7a2f00',
  background: '#fde8d8',
  border: '1px solid #f0bf9a',
  borderRadius: 4,
  padding: '6px 8px',
  marginBottom: 4,
  fontSize: '0.8rem',
  lineHeight: 1.25
}

const deleteDialogActionsStyle = {
  padding: '6px 10px 10px'
}

const deleteActionButtonStyle = (isDelete, disabled = false) => {
  return {
    textTransform: 'none',
    color: isDelete ? (disabled ? '#b89a9a' : '#b00020') : '#455560'
  }
}

const createActionButtonStyle = (disabled = false) => {
  return {
    textTransform: 'none',
    color: disabled ? '#9cb2c4' : '#0f5ea6'
  }
}

const renameActionButtonStyle = (disabled = false) => {
  return {
    textTransform: 'none',
    color: disabled ? '#9cb2c4' : '#0f5ea6'
  }
}

const dialogActionIconStyle = {
  fontSize: 18
}

function normalizeAttachmentName (name) {
  return String(name || '')
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function stopEventPropagation (event) {
  event.stopPropagation()
}

function getFolderContentCount (item) {
  if (!item || (item.type !== 'folder' && item.type !== 'rootfolder')) {
    return 0
  }

  if (Array.isArray(item.children) && item.children.length > 0) {
    return item.children.length
  }

  const count = Number(item.itemCount)
  if (Number.isFinite(count) && count > 0) {
    return count
  }

  return 0
}

function parseAttachmentNames (text) {
  return String(text || '')
    .split(/[\n,;\t]+/)
    .map((token) => token.trim().replace(/^"|"$/g, ''))
    .filter(Boolean)
}

function getDroppedAttachmentNames (event) {
  const dataTransfer = event?.dataTransfer
  logDrop('attachment-drop: dataTransfer', dataTransfer)
  const types = Array.from(dataTransfer?.types || [])
  const candidates = []
  const fileNameCandidates = []

  logDrop('attachment-drop: dataTransfer types', types)

  types.forEach((type) => {
    try {
      const value = dataTransfer.getData(type)
      if (typeof value === 'string' && value.trim().length > 0) {
        logDrop('attachment-drop: payload sample', {
          type,
          value: value.slice(0, 200)
        })
        candidates.push(value)
      }
    } catch (err) {
      logDrop('attachment-drop: failed to read payload type', { type, err })
    }
  })

  if (candidates.length === 0) {
    try {
      const fallback = dataTransfer.getData('text/plain')
      if (typeof fallback === 'string' && fallback.trim().length > 0) {
        logDrop('attachment-drop: fallback text/plain sample', fallback.slice(0, 200))
        candidates.push(fallback)
      }
    } catch (err) {
      logDrop('attachment-drop: failed to read text/plain payload', err)
    }
  }

  const droppedFiles = Array.from(dataTransfer?.files || [])
  droppedFiles.forEach((file) => {
    if (typeof file?.name === 'string' && file.name.trim().length > 0) {
      fileNameCandidates.push(file.name.trim())
    }
  })

  const droppedItems = Array.from(dataTransfer?.items || [])
  droppedItems.forEach((item) => {
    if (typeof item?.getAsFile === 'function') {
      const file = item.getAsFile()
      if (typeof file?.name === 'string' && file.name.trim().length > 0) {
        fileNameCandidates.push(file.name.trim())
      }
    }
  })

  if (fileNameCandidates.length > 0) {
    logDrop('attachment-drop: file names from DataTransfer files/items', fileNameCandidates)
  }

  const namesFromText = candidates.flatMap(parseAttachmentNames)
  const names = namesFromText
    .concat(fileNameCandidates)
    .map(normalizeAttachmentName)
    .filter(Boolean)

  return Array.from(new Set(names))
}

function getDroppedFiles (event) {
  const dataTransfer = event?.dataTransfer
  const droppedFiles = Array.from(dataTransfer?.files || [])
  const files = []
  const seen = new Set()

  droppedFiles.forEach((file) => {
    if (!file) return
    const key = `${String(file.name || '')}:${Number(file.size) || 0}`
    if (!seen.has(key)) {
      seen.add(key)
      files.push(file)
    }
  })

  const droppedItems = Array.from(dataTransfer?.items || [])
  droppedItems.forEach((item) => {
    if (!item || typeof item.getAsFile !== 'function') {
      return
    }
    const file = item.getAsFile()
    if (!file) {
      return
    }
    const key = `${String(file.name || '')}:${Number(file.size) || 0}`
    if (!seen.has(key)) {
      seen.add(key)
      files.push(file)
    }
  })

  return files
}

function getDocumentLibraryRelativePath (item) {
  const itemName = String(item?.name || '').trim()
  const isFile = item?.type === 'file'

  if (typeof item?.path === 'string' && item.path.length > 0) {
    const normalizedPath = normalizeDocumentLibraryPath(item.path)
    return ensureFileNameInRelativePath(normalizedPath, itemName, isFile)
  }

  const value = item?.serverRelativeUrl || item?.uri
  if (typeof value !== 'string' || value.length === 0) {
    return ''
  }

  try {
    const path = value.startsWith('http') ? new URL(value).pathname : value
    const normalizedPath = normalizeDocumentLibraryPath(decodeURIComponent(path))
    return ensureFileNameInRelativePath(normalizedPath, itemName, isFile)
  } catch (err) {
    const normalizedPath = normalizeDocumentLibraryPath(String(value))
    return ensureFileNameInRelativePath(normalizedPath, itemName, isFile)
  }
}

function ensureFileNameInRelativePath (relativePath, itemName, isFile) {
  const normalizedPath = String(relativePath || '').replace(/\/+$/, '')
  if (!isFile || !itemName) {
    return normalizedPath
  }

  const pathSegments = normalizedPath.split('/').filter(Boolean)
  const lastSegment = pathSegments[pathSegments.length - 1] || ''
  if (lastSegment === itemName) {
    return normalizedPath
  }

  if (!normalizedPath) {
    return itemName
  }

  return `${normalizedPath}/${itemName}`
}

function formatInfoBoxRelativePath (value, options = {}) {
  const trimmed = String(value || '').trim()
  const withoutEdges = trimmed.replace(/^\/+|\/+$/g, '')
  const trailingSlash = options.trailingSlash !== false

  if (!withoutEdges) {
    return '/'
  }

  const elements = withoutEdges.split('/').filter(Boolean)
  const joined = `/${elements.join('/')}`
  return trailingSlash ? `${joined}/` : joined
}

function joinRelativePathElements (basePath, nextElement) {
  const base = String(basePath || '').replace(/^\/+|\/+$/g, '')
  const next = String(nextElement || '').trim().replace(/^\/+|\/+$/g, '')

  if (!base) {
    return next
  }
  if (!next) {
    return base
  }

  return `${base}/${next}`
}

function replaceLastRelativePathElement (path, nextLeaf) {
  const normalizedPath = String(path || '').replace(/^\/+|\/+$/g, '')
  const normalizedLeaf = String(nextLeaf || '').trim().replace(/^\/+|\/+$/g, '')
  if (!normalizedPath) {
    return normalizedLeaf
  }

  const segments = normalizedPath.split('/').filter(Boolean)
  if (segments.length === 0) {
    return normalizedLeaf
  }

  if (!normalizedLeaf) {
    return normalizedPath
  }

  segments[segments.length - 1] = normalizedLeaf
  return segments.join('/')
}

function normalizeDocumentLibraryPath (value) {
  const trimmed = String(value || '').replace(/^\/+/, '')
  if (!trimmed) {
    return ''
  }

  const segments = trimmed.split('/').filter(Boolean)
  const filesIndex = segments.findIndex((segment) => /^files(?:_.+)?$/i.test(segment))

  if (filesIndex >= 0) {
    return segments.slice(filesIndex + 1).join('/')
  }

  return trimmed
}

function getLockedFileExtension (name, itemType) {
  if (itemType !== 'file') {
    return ''
  }

  const fileName = String(name || '')
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex <= 0 || dotIndex >= fileName.length - 1) {
    return ''
  }

  return fileName.slice(dotIndex)
}

function getRenameEditableName (name, itemType) {
  const fileName = String(name || '')
  const extension = getLockedFileExtension(fileName, itemType)
  if (!extension) {
    return fileName
  }

  return fileName.slice(0, fileName.length - extension.length)
}

function composeRenameFileName (editableName, extension) {
  const baseName = String(editableName || '').trim()
  if (!extension) {
    return baseName
  }

  return `${baseName}${extension}`
}

function normalizeEditableExtension (value) {
  const raw = String(value || '').trim()
  if (!raw) {
    return ''
  }

  const compact = raw.replace(/\s+/g, '').replace(/\.+/g, '.')
  const normalized = compact.startsWith('.') ? compact : `.${compact}`
  return normalized.toLowerCase()
}

function hasIllegalSharePointNameChars (value) {
  const raw = String(value || '')
  if (!raw.length) {
    return false
  }

  // SharePoint folder names cannot include these reserved characters.
  const forbidden = /[<>:"/\\|?*#%]/
  return forbidden.test(raw)
}

function logDrop (message, payload) {
  if (DROP_DEBUG) {
    console.info(message, payload)
  }
}

const mapStateToProps = (state) => {
  const {
    selectedFileNode,
    trees,
    searchValue,
    searchResults,
    showSearchResults,
    isSearching
  } = state.files

  const getFileMeta = (item) => {
    const DEFAULT = { expand: {}, items: [] }
    if (item) {
      return trees[`${item.type}:${item.id}`] || DEFAULT
    } else {
      return DEFAULT
    }
  }

  const { expand, items } = getFileMeta(state.selected.current)

  return {
    strings: state.locale.strings,
    selectedFileNode,
    selectedFileNodes: state.files.selectedFileNodes,
    selected: state.selected,
    expand,
    items,
    page: state.page,
    searchValue,
    searchResults,
    showSearchResults,
    isSearching,
    userData: state.login.userData
  }
}

const mapDispatchToProps = (dispatch) => {
  const { files, dialog, save, error } = redux.actions

  const resolveDropMetadataFields = () => {
    const state = redux.store.getState()
    const contentTypes = Array.isArray(state.contentType?.items) ? state.contentType.items : []
    const properties = state.save?.documentProperties && typeof state.save.documentProperties === 'object'
      ? state.save.documentProperties
      : {}

    const allowedKeys = contentTypes
      .map(item => String(item?.field_internal_name || '').trim())
      .filter(Boolean)

    if (allowedKeys.length > 0) {
      return allowedKeys.reduce((acc, key) => {
        if (typeof properties[key] !== 'undefined') {
          acc[key] = properties[key]
        }
        return acc
      }, {})
    }

    return Object.keys(properties).reduce((acc, key) => {
      if (key.startsWith('pdscol_') && typeof properties[key] !== 'undefined') {
        acc[key] = properties[key]
      }
      return acc
    }, {})
  }

  const refreshFolder = (siteItem, folderItem) => {
    if (folderItem?.type === 'rootfolder') {
      dispatch(files.getRootFolder(siteItem))
    } else if (folderItem?.type === 'folder') {
      dispatch(files.getSubFolder(siteItem, folderItem))
    }
  }

  const showDropSaveSuccess = () => {
    dispatch(error.showFeedback('DROP_SAVE_SUCCESS', '', 'success'))
  }

  return {
    onSetSelectedFolder: (folderItem, options) => {
      dispatch(files.setSelectedFolder(folderItem, options))
    },
    onGetRootFolder: (siteItem) => {
      dispatch(files.getRootFolder(siteItem))
    },
    onGetSubFolders: (siteItem, folderItem) => {
      dispatch(files.getSubFolder(siteItem, folderItem))
    },
    onSetSelectedFile: (fileItem) => {
      dispatch(files.setSelectedFile(fileItem))
    },
    onSetSelectedFiles: (fileItems) => {
      dispatch(files.setSelectedFiles(fileItems))
    },
    onSearchValueChange: (value) => {
      dispatch(files.setSearchValue(value))
    },
    onStartSearch: (current, searchValue) => {
      dispatch(files.searchFiles(current, searchValue))
    },
    onResetSearch: () => {
      dispatch(files.resetSearch())
    },
    onCreateFolder: (siteItem, item, name) => {
      dispatch(files.createFolder(siteItem, item, name))
    },
    onAttachFile: (fileItem) => {
      dispatch(files.attachFile(fileItem))
    },
    onAttachFiles: (fileItems) => {
      dispatch(files.attachFiles(fileItems))
    },
    onAttachFileAsPdf: (fileItem) => {
      dispatch(files.attachFileAsPdf(fileItem))
    },
    onAttachFileAsPdfAndSaveCopy: (fileItem) => {
      dispatch(files.attachFileAsPdfAndSaveCopy(fileItem))
    },
    onSaveFileAsPdfCopy: (fileItem) => {
      dispatch(files.saveFileAsPdfCopy(fileItem))
    },
    onRenameItem: (siteItem, item, newName) => {
      dispatch(files.renameItem(siteItem, item, newName))
    },
    onDeleteItem: (siteItem, item) => {
      dispatch(files.deleteItem(siteItem, item))
    },
    onDeleteItemPermanently: (siteItem, item) => {
      dispatch(files.deleteItemPermanently(siteItem, item))
    },
    onDropSaveCurrentEmail: async (siteItem, folderItem, onOverwriteConflict) => {
      const fileName = emailFileNameOutlookReadMode()
      const metadataFields = resolveDropMetadataFields()
      dispatch(save.uploadFile(siteItem, folderItem, fileName, metadataFields, false, () => {
        refreshFolder(siteItem, folderItem)
        showDropSaveSuccess()
      }, {
        showProgress: false,
        overwrite: false,
        onOverwriteConflict
      }))
      return true
    },
    onDropSaveFiles: async (siteItem, folderItem, droppedFiles, onOverwriteConflict) => {
      const preparedFiles = (Array.isArray(droppedFiles) ? droppedFiles : []).filter(Boolean)

      if (preparedFiles.length === 0) {
        return false
      }

      const metadataFields = resolveDropMetadataFields()
      dispatch(save.uploadDroppedFiles(siteItem, folderItem, preparedFiles, metadataFields, () => {
        refreshFolder(siteItem, folderItem)
        showDropSaveSuccess()
      }, {
        showProgress: false,
        overwrite: false,
        onOverwriteConflict
      }))
      return true
    },
    onShowRenameAttachmentsDialog: (siteItem, fileItem, attachments, dropInfo) => {
      dispatch(dialog.showRenameAttachmentsDialog(siteItem, fileItem, attachments, dropInfo))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FileView)

function flattenVisibleFileItems (treeItems, expandState) {
  const visibleFiles = []

  const visit = (items) => {
    const list = Array.isArray(items) ? items : []
    list.forEach((item) => {
      if (!item) return

      if (item.type === 'file') {
        visibleFiles.push(item)
      }

      const hasChildren = Array.isArray(item.children) && item.children.length > 0
      if (!hasChildren) return

      const expanded = item.type === 'rootfolder' || !!expandState?.[item.id]
      if (expanded) {
        visit(item.children)
      }
    })
  }

  visit(treeItems)
  return visibleFiles
}

function getFileSelectionRange (fileItems, anchorId, targetId) {
  const start = fileItems.findIndex((item) => item?.id === anchorId)
  const end = fileItems.findIndex((item) => item?.id === targetId)

  if (start < 0 || end < 0) {
    return fileItems.filter((item) => item?.id === targetId)
  }

  const from = Math.min(start, end)
  const to = Math.max(start, end)
  return fileItems.slice(from, to + 1)
}
